<script setup>
import { computed, useId } from 'vue';
import XlDropdown from '../xl/XlDropdown.vue';
import XlField from '../xl/XlField.vue';
import XlTooltip from '../xl/XlTooltip.vue';
import { TERM_TIPS } from '../../helpers/termTips';
import { clampDays } from './sequenceChain';

const props = defineProps({
  modelValue: { type: Object, required: true },
  number: { type: Number, required: true },
  heading: { type: String, required: true },
  templates: { type: Array, default: () => [] },
  renewal: { type: Boolean, default: true },
});

const emit = defineEmits(['update:modelValue', 'remove']);

const offsetId = useId();

const WHEN_OPTIONS = [
  { value: 'before', label: 'before renewal' },
  { value: 'after', label: 'after renewal' },
];

const patch = (changes) => emit('update:modelValue', { ...props.modelValue, ...changes });

const templateOptions = computed(() => props.templates.map((template) => ({ value: template.id, label: template.name })));
</script>

<template>
  <div class="step-card" data-testid="sequence-step">
    <div class="step-card__head">
      <span class="step-card__num">{{ props.number }}</span>
      <span class="step-card__heading" data-testid="step-heading">{{ props.heading }}</span>
      <button
        type="button"
        class="step-card__remove"
        data-testid="step-remove"
        title="Remove this step"
        aria-label="Remove this step"
        @click="emit('remove')"
      >
        <svg width="15" height="15" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path d="M5.5 5.5l9 9M14.5 5.5l-9 9" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
        </svg>
      </button>
    </div>

    <div class="step-card__grid">
      <template v-if="props.renewal">
        <label class="step-card__field" :for="offsetId">
          <span class="step-card__caption">Days</span>
          <XlField
            :id="offsetId"
            type="number"
            name="step-offset"
            data-testid="step-offset"
            aria-label="Days"
            min="0"
            max="730"
            :model-value="props.modelValue.offset_days"
            @change="(event) => patch({ offset_days: clampDays(event.target.value) })"
          />
        </label>
        <div class="step-card__field">
          <span class="step-card__caption"><XlTooltip :text="TERM_TIPS.Renewal" underline>When</XlTooltip></span>
          <XlDropdown
            data-testid="step-direction"
            aria-label="When"
            :options="WHEN_OPTIONS"
            :model-value="props.modelValue.direction"
            @update:model-value="(value) => patch({ direction: value })"
          />
        </div>
      </template>
      <div v-else-if="$slots.date" class="step-card__field">
        <span class="step-card__caption">Date</span>
        <slot name="date" />
      </div>

      <div class="step-card__field">
        <span class="step-card__caption">Template</span>
        <XlDropdown
          data-testid="step-template"
          aria-label="Template"
          :options="templateOptions"
          :model-value="props.modelValue.template_id"
          @update:model-value="(value) => patch({ template_id: value })"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.step-card {
  border: 1px solid var(--xl-color-border, #ede7f0);
  border-radius: 13px;
  background: #fff;
  color: var(--xl-color-on-surface);
  padding: 12px 13px 13px;

  &__head {
    display: flex;
    align-items: center;
    gap: 9px;
    margin-bottom: 11px;
  }

  &__num {
    display: grid;
    place-items: center;
    flex: 0 0 23px;
    width: 23px;
    height: 23px;
    border-radius: 50%;
    background: var(--xl-color-primary-container);
    color: var(--xl-color-primary);
    font-size: 11.5px;
    font-weight: 700;
  }

  &__heading {
    flex: 1;
    min-width: 0;
    font-size: 13.5px;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: var(--xl-color-primary-text);
  }

  &__remove {
    display: grid;
    place-items: center;
    flex: 0 0 28px;
    width: 28px;
    height: 28px;
    padding: 0;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: var(--xl-color-outline-variant);
    cursor: pointer;
    transition: background 0.12s, color 0.12s;

    &:hover {
      background: var(--xl-color-error-container);
      color: var(--xl-color-error);
    }
  }

  &__grid {
    display: grid;
    gap: 10px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  // The board's fields sit inline in their label, so each line box leaves 5px
  // under its control.
  &__field {
    display: block;
    min-width: 0;
    padding-bottom: 5px;
  }

  &__field :deep(.xl-dropdown__control) { font-weight: 600; }

  &__field :deep(.xl-dt__control) {
    padding-left: 14px !important;
    padding-right: 38px !important;
    font-size: 14.5px;
    font-weight: 600;
  }

  &__field :deep(.xl-dt__control::placeholder) { font-weight: 400; }

  &__caption {
    display: block;
    margin-bottom: 5px;
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: var(--xl-color-outline-variant);
  }

  &__field :deep(.xl-field),
  &__field :deep(.xl-dropdown) {
    width: 100%;
  }
}
</style>
